package org.matthewo.router

import js.objects.Object
import js.objects.ReadonlyRecord
import js.objects.Record
import js.objects.buildReadonlyRecord
import js.promise.await
import js.reflect.unsafeCast
import js.uri.decodeURIComponent
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import tanstack.react.router.*
import tanstack.react.router.RootRouteOptions
import tanstack.react.router.RouteOptions
import tanstack.react.router.RouterOptions
import tanstack.router.core.*
import web.mouse.MAIN
import web.mouse.MouseButton

operator fun String.div(path: String) = "${this.trimEnd('/')}/${path.trimStart('/')}"

operator fun String.div(param: ParamName) = "${this.trimEnd('/')}/$$param"

operator fun ParamName.div(path: String) = "/$$this/${path.trimStart('/')}"

operator fun RoutePath.div(param: ParamName) = toString() / param

operator fun RoutePath.div(path: String) = toString() / path

@OptIn(ExperimentalWasmJsInterop::class)
operator fun <T : JsAny> Record<T, *>.contains(key: T) = this[key] != null

@OptIn(ExperimentalWasmJsInterop::class)
operator fun <T : JsAny> ReadonlyRecord<T, *>.contains(key: T) = this[key] != null

inline fun RoutePath(
    param: ParamName,
): RoutePath =
    unsafeCast("$$param")

// the rendered anchor href is built from `to`, so an external url has to be set there,
// while `href` is what an imperative navigate uses to leave the site
fun NavigateOptions.toExternal(url: String) {
    to = RoutePath(url)
    href = url
}

@DslMarker
@Target(AnnotationTarget.CLASS)
annotation class TSRouteBuilderMarker

@TSRouteBuilderMarker
class TSRouteBuilder(private val parent: BaseRoute<*>) {
    internal val children = mutableListOf<Route>()

    fun TSRoute(path: RoutePath, component: RouteComponent? = null, builder: TSRouteBuilder.() -> Unit = {}): Route {
        val route = createRoute(
            RouteOptions(
                getParentRoute = { parent },
                path = path,
                component = component,
            )
        )
        val routeBuilder = TSRouteBuilder(route)
        routeBuilder.builder()
        route.addChildren(routeBuilder.children.toTypedArray())
        children += route
        return route
    }

    fun TSRedirect(path: RoutePath, toPath: RoutePath): Route {
        val route = createRoute(
            RouteOptions(
                getParentRoute = { parent },
                path = path,
                beforeLoad = { redirect { to = toPath } },
            )
        )
        children += route
        return route
    }
}

fun TSRouter(component: RouteComponent? = null, builder: TSRouteBuilder.() -> Unit): Router {
    val rootRoute = createRootRoute(
        RootRouteOptions(
            component = component,
        )
    )
    val routeBuilder = TSRouteBuilder(rootRoute)
    routeBuilder.builder()
    rootRoute.addChildren(routeBuilder.children.toTypedArray())
    return createRouter(
        options = RouterOptions(
            routeTree = rootRoute,
            stringifySearch = plainStringifySearch,
            parseSearch = plainParseSearch,
        ),
    )
}

// only the characters that would otherwise break the URL
private fun escape(value: String): String = value
    .replace("%", "%25")
    .replace("#", "%23")
    .replace("&", "%26")
    .replace("+", "%2B")

@OptIn(ExperimentalWasmJsInterop::class)
private val plainStringifySearch: SearchSerializer = { search ->
    val query = Object.keys(search).toList()
        .mapNotNull { key ->
            val value = search[key] ?: return@mapNotNull null
            "${escape(key)}${if (value == Unit) "" else "=${escape(value.toString())}"}"
        }
        .joinToString("&")

    if (query.isEmpty()) "" else "?$query"
}

private val plainParseSearch: SearchParser = { searchStr ->
    buildReadonlyRecord<String, Any?> {
        searchStr.removePrefix("?")
            .split("&")
            .filter { it.isNotEmpty() }
            .forEach { pair ->
                val parts = pair.split('=', limit = 2)
                this[decodeURIComponent(parts[0])] =
                    parts.getOrNull(1)?.let { decodeURIComponent(it) } ?: Unit
            }
    }
}

context(link: LinkComponentProps)
fun CoroutineScope.replaceAndNavigate(navigate: UseNavigateResult, replacement: NavigateOptions.() -> Unit, to: NavigateOptions.() -> Unit) {
    link.onClick = { event ->
        if (MouseButton.MAIN == event.button && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
            event.preventDefault()
            launch {
                navigate {
                    replacement()
                    replace = true
                }.await()
                navigate {
                    to()
                }
            }
        }
    }
    link.to()
}
